import { CommonModule } from '@angular/common';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { inject, NgModule } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { CoreModule, provideErrorTracking } from '@galaxy/core';
import { LexiconModule } from '@galaxy/lexicon';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyNavControlService, GalaxyNavModule } from '@vendasta/galaxy/nav';
import { GalaxyPageOptions, GALAXY_PAGE_OPTIONS } from '@vendasta/galaxy/page';
import { GalaxyDefaultProviderOverrides } from '@vendasta/galaxy/provider-default-overrides';
import { ProductAnalyticsModule } from '@vendasta/product-analytics';
import { CoreSharedModule } from '@vendasta/shared';
import { UIKitModule } from '@vendasta/uikit';
// import { HighchartsStatic } from 'angular2-highcharts/dist/HighchartsService';
import baseTranslation from '../assets/i18n/en_devel.json';
import { AccountsService } from './accounts.service';
import { AppComponent } from './app.component';
import { AppService } from './app.service';
import { WEBLATE_COMPONENT_NAME } from './constants';
import { AccountApiService, AccountService } from './core/account';
import { ApiErrorFactory } from './core/api-errors';
import { AppConfigService } from './core/app-config.service';
import { DefaultDomainUtility, NonDefaultDomainUtility } from './core/domain-utility';
import { FeatureFlagService } from './core/feature-flag.service';
import { GetProService } from './core/get-pro/get-pro.service';
import { UpgradeCTADialogService } from './core/get-pro/pro-upgrade-cta.service';
import { IAMTokenService } from './core/iam-token.service';
import { MultiLocationAnalyticsModule } from './core/multi_location_analytics_sdk/multi-location-analytics.module';
import { ProductSettingsService } from './core/product-settings';
import { SiteApiService, SiteService } from './core/site';
import { UserService } from './core/user.service';
import { AppBrandingService } from './core/white-label/app-branding.service';
import { AccountIdLoggedInCanActivate, SiteIdLoggedInCanActivateChild, UpgradeCTAGuard } from './guards';
import { RedirectGuard } from './guards/redirect.guard';
import { SelectedAccountService } from './selected-account.service';
import { SelectedSiteService } from './selected-site.service';
import { AppAtlasModule } from './views/atlas/atlas.module';
import { BackupManagerModule } from './views/backup/backup-manager.module';
import { CreateFromTemplateModule } from './views/create-from-template';
import { CreateNewTemplateModule } from './views/create-new-template';
import { DomainManagerModule } from './views/domain';
import { EmailsModule } from './views/emails/emails.module';
import { EntryComponent } from './views/entry/entry.component';
import { RedirectEntryComponent } from './views/entry/redirect-entry.component';
import { ErrorLogsModule } from './views/error-logs/error-logs.module';
import { AppFailureComponent } from './views/failure/app-failure.component';
import { HomeModule } from './views/home/home.module';
import { ImportModule } from './views/import';
import { AppInsufficientPermissionComponent } from './views/insufficient-permission/app-insufficient-permission.component';
import { ManageTemplatesModule } from './views/manage-templates';
import { RestoreInProgressModule } from './views/restore-in-progress';
import { routes } from './views/routes';
import { SftpModule } from './views/sftp/sftp.module';
import { StagingModule } from './views/staging';
import { provideProtoTransport } from '@vendasta/protos';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
declare let require: any;

// Factory Function
// export function highchartsFactory(): any {
//   return require('highcharts');
// }

@NgModule({
  declarations: [
    AppComponent,
    AppFailureComponent,
    AppInsufficientPermissionComponent,
    EntryComponent,
    RedirectEntryComponent,
  ],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    MatMenuModule,
    CommonModule,
    BrowserAnimationsModule,
    RouterModule.forRoot(routes, {}),
    ProductAnalyticsModule,
    MatCardModule,
    MatButtonModule,
    TranslateModule,
    LexiconModule.forRoot({
      componentName: WEBLATE_COMPONENT_NAME,
      baseTranslation: baseTranslation,
    }),
    CoreModule,
    UIKitModule,
    MultiLocationAnalyticsModule,
    CoreSharedModule,
    CreateFromTemplateModule,
    HomeModule,
    ManageTemplatesModule,
    ImportModule,
    DomainManagerModule,
    BackupManagerModule,
    StagingModule,
    EmailsModule,
    ErrorLogsModule,
    SftpModule,
    RestoreInProgressModule,
    CreateNewTemplateModule,
    AppAtlasModule,
    GalaxyNavModule,
    MatIconModule,
  ],
  providers: [
    provideProtoTransport(),
    provideErrorTracking(),
    ...GalaxyDefaultProviderOverrides,
    SelectedSiteService,
    SelectedAccountService,
    SiteService,
    SiteApiService,
    AccountApiService,
    AccountService,
    AccountIdLoggedInCanActivate,
    SiteIdLoggedInCanActivateChild,
    UpgradeCTAGuard,
    RedirectGuard,
    AppService,
    DefaultDomainUtility,
    NonDefaultDomainUtility,
    IAMTokenService,
    // {
    //   provide: HighchartsStatic,
    //   useFactory: highchartsFactory,
    // },
    ApiErrorFactory,
    AccountsService,
    UpgradeCTADialogService,
    GetProService,
    FeatureFlagService,
    AppConfigService,
    UserService,
    AppBrandingService,
    ProductSettingsService,
    {
      provide: GALAXY_PAGE_OPTIONS,
      useFactory: galaxyNavOptionsFactory,
    },
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {}

export function galaxyNavOptionsFactory(): GalaxyPageOptions {
  const navControlService = inject(GalaxyNavControlService);
  return {
    showToggle: true,
    toggleNav: () => navControlService.toggle(),
  };
}
